import axios from "axios";
import { session } from "./session";
import { refreshCurrentSession } from "../../../shared-client/guestSession.mjs";
import { ensureWorkSubmissionKey, createWorkSubmissionJournal } from "../../../shared-client/workBilling.mjs";
// Seam: config.js, store/index.js and router.js are per-app (they live in
// the consuming app's src/, not in this shared library), so they are reached
// through the @/ alias - which webpack resolves against the CONSUMING app's
// src/ - rather than by relative path.
import config from "@/config";
import store from "@/store";
import router from "@/router";

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: config.api.baseURL,
  // No timeout - let the server handle timeouts
  headers: {
    "Content-Type": "application/json"
  }
});

// Multi-app site resolution (dev only): the gateway's SiteContextMiddleware
// resolves the site from the Host header in production, but falls back to
// the X-Site-Slug header (then SITE_SLUG env, then the default site) when
// Host doesn't match any site row - e.g. every app's dev server here runs on
// localhost under a different port. config.SITE_SLUG is undefined for the
// default app (it relies on the Host/default-site fallback) and set by
// per-app config.js modules (e.g. UpscalePro's "upscale") so their dev
// requests resolve to the right site. Set as an axios default header so it
// rides every request this instance makes, without touching the auth
// interceptor below.
if (config.SITE_SLUG) {
  apiClient.defaults.headers.common["X-Site-Slug"] = config.SITE_SLUG;
}

const workJournal = createWorkSubmissionJournal(session);

// Flag to prevent multiple token refresh attempts at once
let isRefreshing = false;
// Queue of requests to retry after token refresh
let refreshQueue = [];

// Function to process the queue of requests waiting for token refresh
const processQueue = (error, token = null) => {
  refreshQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  // Reset the queue
  refreshQueue = [];
};

// Function to refresh the token
const refreshAuthToken = () => refreshCurrentSession(session, async token => {
  const response = await axios.post(`${config.api.baseURL}/auth/refresh-token`, {}, {
    headers: { Authorization: `Bearer ${token}`,
      ...(config.SITE_SLUG ? { "X-Site-Slug": config.SITE_SLUG } : {}) }
  });
  return response.data.access_token;
}, token => store.commit("SET_TOKEN", token));

// Add authentication interceptor function
const addAuthInterceptor = client => {
  // Request interceptor
  client.interceptors.request.use(
    async config => {
      const user = store.getters.currentUser;
      await workJournal.prepare(config, [config.baseURL, user && user.site_id, user && user.id]);
      ensureWorkSubmissionKey(config, () => {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        return Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
      });
      const token = session.getItem("token");
      if (token && !config.skipSessionAuth) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for handling token expiration
  client.interceptors.response.use(
    response => {
      workJournal.finish(response.config, response.status);
      return response;
    },
    async error => {
      const originalRequest = error.config;
      if (!error.response || error.response.status !== 401)
        workJournal.finish(originalRequest, error.response && error.response.status);
      if ((!error.response || error.response.status >= 500) && originalRequest && originalRequest._workJournal) {
        error.workPending = true;
        const detail = error.response && error.response.data && error.response.data.detail;
        if (detail && detail.code === "submission_in_progress") {
          detail.message = "Your submission is still being accepted. Try again with the same settings to resume it.";
        }
      }

      // If the error is not 401 or the request has already been retried, reject
      if (
        !error.response ||
        error.response.status !== 401 ||
        !originalRequest || originalRequest._retry || originalRequest.skipSessionAuth || originalRequest.skipAuthRecovery ||
        originalRequest.url === "/auth/token" || originalRequest.url === "/auth/register" ||
        originalRequest.url === "/auth/anonymous" || originalRequest.url === "/users/me/email-change"
      ) {
        return Promise.reject(error);
      }

      // Mark the request as retried to prevent infinite loops
      originalRequest._retry = true;

      // If token refresh is already in progress, add this request to the queue
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      // Start the token refresh process
      isRefreshing = true;

      try {
        // Try to refresh the token
        const newToken = await refreshAuthToken();

        // Update the authorization header with the new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        // Process any queued requests with the new token
        processQueue(null, newToken);

        // Retry the original request with the new token
        return client(originalRequest);
      } catch (refreshError) {
        // If token refresh fails, process the queue with the error
        processQueue(refreshError);

        store.dispatch("logout");
        router.push("/login");

        return Promise.reject(refreshError);
      } finally {
        // Reset the refreshing flag
        isRefreshing = false;
      }
    }
  );
};

// Add interceptors to the API client
addAuthInterceptor(apiClient);

// Secondary guest traffic cannot refresh/log out or overwrite the primary member.
export function createGuestClient(token, onRefresh) {
  const guest = axios.create({ baseURL: config.api.baseURL, headers: {
    Authorization: "Bearer " + token,
    ...(config.SITE_SLUG ? { "X-Site-Slug": config.SITE_SLUG } : {})
  }});
  let refreshing = null;
  guest.interceptors.response.use(response => response, async error => {
    if (!error.response || error.response.status !== 401 || error.config._retry) throw error;
    error.config._retry = true;
    if (!refreshing) {
      refreshing = axios.post(config.api.baseURL + "/auth/refresh-token", {}, {
        headers: { Authorization: "Bearer " + token,
          ...(config.SITE_SLUG ? { "X-Site-Slug": config.SITE_SLUG } : {}) }
      }).then(response => {
        token = response.data.access_token;
        guest.defaults.headers.Authorization = "Bearer " + token;
        onRefresh(token);
        return token;
      }).finally(() => { refreshing = null; });
    }
    error.config.headers.Authorization = "Bearer " + await refreshing;
    return guest(error.config);
  });
  return guest;
}

export { apiClient };

const apiService = {
  // Utility method to get current user ID
  getCurrentUserId() {
    const user = JSON.parse(session.getItem("user") || "{}");
    return user.id || null;
  },

  // Authentication endpoints
  async register(userData) {
    return apiClient.post("/auth/register", userData);
  },

  // Anonymous trial (per-site opt-in): create/resume a guest session.
  // 404 when the site has not enabled SITE_<SLUG>_ANONYMOUS_TRIAL.
  anonymousSession(token) {
    return apiClient.post("/auth/anonymous", token ? { guest_token: token } : {});
  },

  async login(email, password) {
    const formData = new FormData();
    formData.append("username", email); // OAuth2 uses 'username' field
    formData.append("password", password);

    return apiClient.post("/auth/token", formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
  },

  // "Continue with Google": the GIS credential plus optional ref_token /
  // anonymous_token (guest upgrade). 503 on apps without a Google client id.
  async googleLogin(payload) {
    return apiClient.post("/auth/google", payload);
  },

  async forgotPassword(email) {
    return apiClient.post("/auth/forgot-password", { email });
  },

  async resetPassword(token, password, passwordConfirm) {
    return apiClient.post("/auth/reset-password", {
      token,
      password,
      password_confirm: passwordConfirm
    });
  },

  async verifyEmail(token) {
    return apiClient.post("/auth/verify-email", { token });
  },

  async resendVerificationEmail(email) {
    return apiClient.post("/auth/resend-verification", { email });
  },

  async getCurrentUser() {
    return apiClient.get("/auth/me");
  },

  async refreshToken() {
    const token = await refreshAuthToken();
    return { data: { access_token: token, token_type: "bearer" } };
  },

  // User endpoints
  async getUser(userId) {
    return apiClient.get(`/users/${userId}`);
  },

  async assumeIdentity(userId) {
    return apiClient.post(`/admin/users/${userId}/assume-identity`);
  },

  async updateProfile(userData) {
    return apiClient.patch("/users/me", userData);
  },

  // Public, unauthenticated feature flags for the frontend (force-paid, etc.)
  async getPublicConfig() {
    return apiClient.get("/public-config");
  },

  // Subscription endpoints
  async getSubscriptionPlans() {
    return apiClient.get("/subscriptions/plans", {
      params: { is_active: true }
    });
  },

  async getCurrentSubscription() {
    return apiClient.get("/subscriptions/current");
  },

  async createCheckoutSession(planId, billingCycle, successUrl, cancelUrl) {
    return apiClient.post("/subscriptions/checkout", {
      plan_id: planId,
      billing_cycle: billingCycle,
      success_url: successUrl,
      cancel_url: cancelUrl
    });
  },

  async cancelSubscription() {
    return apiClient.post("/subscriptions/cancel");
  },

  async resumeSubscription() {
    return apiClient.post("/subscriptions/resume");
  },

  async downgradeToFreePlan() {
    return apiClient.post("/subscriptions/downgrade-to-free");
  },

  async submitCancellationFeedback(reason, comment) {
    return apiClient.post("/subscriptions/cancellation-feedback", {
      reason: reason,
      comment: comment || null
    });
  },

  async getAccountPortalUrl() {
    return apiClient.get("/subscriptions/account-portal");
  },

  // ---------------------------------------------------------------------
  // Referrals
  // ---------------------------------------------------------------------
  async getMyReferralCode() {
    return apiClient.get("/referrals/my-code");
  },

  async getReferralProgress() {
    return apiClient.get("/referrals/progress");
  },

  async listReferralInvitations() {
    return apiClient.get("/referrals/invitations");
  },

  async sendReferralInvitation(inviteeEmail, customMessage) {
    return apiClient.post("/referrals/invitations", {
      invitee_email: inviteeEmail,
      custom_message: customMessage || null
    });
  },

  async revokeReferralInvitation(referralId) {
    return apiClient.delete(`/referrals/invitations/${referralId}`);
  },

  async previewReferralToken(token) {
    return apiClient.get(`/referrals/preview/${encodeURIComponent(token)}`);
  },

  // Credit endpoints
  async getCreditBalance() {
    return apiClient.get("/credits/balance");
  },

  async getCreditHistory(limit = 50, offset = 0) {
    return apiClient.get("/credits/history", {
      params: { limit, offset }
    });
  },

  async purchaseCredits(amount, urls) {
    // Default return point is the Billing page; the out-of-credits paywall
    // overrides it so a buyer lands back on their parked conversion.
    const successUrl =
      (urls && urls.successUrl) ||
      `${window.location.origin}/billing?success=true&amount=${amount}`;
    const cancelUrl =
      (urls && urls.cancelUrl) || `${window.location.origin}/billing?canceled=true`;

    return apiClient.post("/credits/purchase", {
      amount,
      success_url: successUrl,
      cancel_url: cancelUrl
    });
  },

  // --- Notifications (bell icon) ---
  async getNotifications(params = {}) {
    return apiClient.get("/notifications", { params });
  },
  async markNotificationRead(notificationId) {
    return apiClient.post(`/notifications/${notificationId}/read`);
  },
  async markAllNotificationsRead() {
    return apiClient.post("/notifications/read-all");
  },
  async deleteNotification(notificationId) {
    return apiClient.delete(`/notifications/${notificationId}`);
  },

  // --- Issue tracking (admin-only screen) ---
  async listIssues(params = {}) {
    return apiClient.get("/issues/", { params });
  },
  async getIssue(issueId) {
    return apiClient.get(`/issues/${issueId}`);
  },
  async updateIssue(issueId, payload) {
    return apiClient.patch(`/issues/${issueId}`, payload);
  },
  async deleteIssue(issueId) {
    return apiClient.delete(`/issues/${issueId}`);
  },

  // ---------------------------------------------------------------------
  // Projects (music-video product)
  // ---------------------------------------------------------------------
  async listProjects(params) {
    // params: { q, limit } - server-side keyword search (title, artist,
    // song filename, lyrics, prompt, story, cast) and/or a result cap.
    return apiClient.get("/projects/", params ? { params } : undefined);
  },
  async deleteProject(projectId) {
    return apiClient.delete(`/projects/${projectId}`);
  },
  async createProject(title, artistName) {
    return apiClient.post("/projects/", {
      title,
      artist_name: artistName
    });
  },
  async getProject(projectId) {
    return apiClient.get(`/projects/${projectId}`);
  },
  async getProjectState(projectId) {
    // One-call feed snapshot: latest job per stage + artifact index (mtimes).
    return apiClient.get(`/projects/${projectId}/state`);
  },

  // --- Review agent (vision agentic loop) ------------------------------
  async getReviewState(projectId) {
    return apiClient.get(`/projects/${projectId}/review-state`);
  },
  async postReviewDecision(projectId, payload) {
    // payload: { interaction_id, decision: 'approve'|'reject', message? }
    return apiClient.post(`/projects/${projectId}/review-decision`, payload);
  },
  // --- Video generation settings ----------------------------------------
  async getVideoSettings(projectId) {
    return apiClient.get(`/projects/${projectId}/video-settings`);
  },
  async updateVideoSettings(projectId, patch) {
    // patch: { generators?, performance_intensity?, global_prompts?,
    //          negative_prompt? } - partial; empty prompt resets to default.
    return apiClient.patch(`/projects/${projectId}/video-settings`, patch);
  },
  async getVideoCostEstimate(projectId, sample = false) {
    return apiClient.get(`/projects/${projectId}/video-cost-estimate`, {
      params: sample ? { sample: true } : {}
    });
  },

  async setReviewModeOverride(projectId, mode) {
    // mode: 'auto' | 'interactive' | null - governs a RUNNING review live.
    return apiClient.post(`/projects/${projectId}/review-mode-override`, {
      mode
    });
  },

  // --- Manual edits to generated artifacts (manual mode) ---------------
  async updateProjectStory(projectId, payload) {
    // payload: { title?, logline?, story_text? }
    return apiClient.patch(`/projects/${projectId}/story`, payload);
  },
  async updateCastPrompt(projectId, payload) {
    // payload: { asset_kind, entity_id, variation_id, prompt }
    return apiClient.patch(`/projects/${projectId}/cast-prompt`, payload);
  },
  async updateClip(projectId, clipIndex, payload) {
    // payload: { prompt?, camera? }
    return apiClient.patch(
      `/projects/${projectId}/clips/${clipIndex}`,
      payload
    );
  },
  async regenerateTarget(projectId, target, options = {}) {
    // Rebuilds ONE asset with the same reference images as the original.
    // review:true leaves the result as a candidate for approval.
    return apiClient.post("/jobs/", {
      project_id: projectId,
      job_type: "regenerate",
      target,
      run_now: true,
      review: !!options.review
    });
  },
  async approveCandidate(projectId, payload) {
    // payload: { target, version }
    return apiClient.post(`/projects/${projectId}/candidates/approve`, payload);
  },
  async discardCandidate(projectId, payload) {
    return apiClient.post(`/projects/${projectId}/candidates/discard`, payload);
  },
  async updateProject(projectId, payload) {
    // payload: { title?, artist_name?, lyrics?, creative_prompt? }
    return apiClient.patch(`/projects/${projectId}`, payload);
  },
  async uploadProjectSong(projectId, file) {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post(`/projects/${projectId}/song`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  async uploadProjectPhotos(projectId, files, role = "singer") {
    const formData = new FormData();
    (files || []).forEach(file => formData.append("files", file));
    formData.append("role", role);
    return apiClient.post(`/projects/${projectId}/photos`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  async setPhotoRole(projectId, file, { role, primary } = {}) {
    return apiClient.patch(`/projects/${projectId}/photos/roles`, {
      file,
      role,
      primary
    });
  },

  // ---------------------------------------------------------------------
  // Generation jobs (pipeline stages)
  // ---------------------------------------------------------------------
  async listGenerationJobs(projectId) {
    return apiClient.get("/jobs/", { params: { project_id: projectId } });
  },
  async createGenerationJob(projectId, jobType, runNow = true) {
    return apiClient.post("/jobs/", {
      project_id: projectId,
      job_type: jobType,
      run_now: runNow
    });
  },
  async getGenerationJobStatus(jobId) {
    return apiClient.get(`/jobs/${jobId}/status`);
  },
  async runGenerationJob(jobId) {
    return apiClient.post(`/jobs/${jobId}/run`);
  },
  async stopGenerationJob(jobId) {
    return apiClient.post(`/jobs/${jobId}/stop`);
  },

  // --- Admin: bulk outreach email ---
  async adminGetEmailConfig() {
    const response = await apiClient.get("/admin/email/config");
    return response.data;
  },
  async adminListUsers() {
    const response = await apiClient.get("/admin/users/");
    return response.data;
  },
  async adminSendBulkEmail(payload) {
    // payload: { recipients: string[], subject, body, is_html, reply_to }
    const response = await apiClient.post("/admin/email/send", payload);
    return response.data;
  }
};

export default apiService;
